import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Search, Lightbulb, Trophy, Quote, ChevronRight } from "lucide-react";
import type { CaseStudySections, CaseStudyMeta } from "@/lib/parseCaseStudyContent";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";

interface CaseStudyLayoutProps {
  sections: CaseStudySections;
  meta: CaseStudyMeta;
  clientLogo?: { src: string; alt: string };
}

const markdownComponents = {
  h3: ({ children, ...props }: any) => (
    <h3 className="text-lg sm:text-xl font-bold text-foreground mt-8 first:mt-0 mb-4" {...props}>{children}</h3>
  ),
  p: ({ children }: any) => (
    <p className="text-foreground/75 leading-[1.9] text-[1.0625rem] mb-5 last:mb-0">{children}</p>
  ),
  ul: ({ children }: any) => (
    <ul className="space-y-3 my-5 list-none pl-0 text-foreground/75 text-[1.0625rem] leading-[1.9] [&_li]:relative [&_li]:pl-6 [&_li]:before:content-[''] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[0.65em] [&_li]:before:w-2 [&_li]:before:h-2 [&_li]:before:rounded-full [&_li]:before:bg-primary/30">{children}</ul>
  ),
  ol: ({ children }: any) => (
    <ol className="space-y-3 my-5 list-decimal pl-5 text-foreground/75 text-[1.0625rem] leading-[1.9]">{children}</ol>
  ),
  img: ({ src, alt }: any) => (
    <img src={src} alt={alt || ""} loading="lazy" className="block my-10 w-full max-w-none rounded-lg border border-border/50 sm:relative sm:left-[calc(50%-3.75rem)] sm:-translate-x-1/2 sm:w-[min(920px,calc(100%+7.5rem))]" />
  ),
  strong: ({ children }: any) => <strong className="text-foreground font-semibold">{children}</strong>,
};

const CaseStudyLayout = ({ sections, clientLogo }: CaseStudyLayoutProps) => {
  const lang = useLanguage();
  const t = uiT(lang);

  const sectionConfig = [
    {
      key: "challenge" as const,
      label: t("csChallenge"),
      subtitle: t("csChallengeDesc"),
      icon: Search,
      color: "var(--primary)",
      gradient: "from-primary/8 to-primary/2",
      iconBg: "bg-primary/10",
      accentBorder: "border-l-primary",
      
    },
    {
      key: "approach" as const,
      label: t("csApproach"),
      subtitle: t("csApproachDesc"),
      icon: Lightbulb,
      color: "var(--primary)",
      gradient: "from-primary/8 to-primary/2",
      iconBg: "bg-primary/10",
      accentBorder: "border-l-primary",
      
    },
    {
      key: "result" as const,
      label: t("csResult"),
      subtitle: t("csResultDesc"),
      icon: Trophy,
      color: "var(--primary)",
      gradient: "from-primary/8 to-primary/2",
      iconBg: "bg-primary/10",
      accentBorder: "border-l-primary",
      
    },
  ];

  return (
    <div>

      {/* Summary */}
      {sections.summary && (
        <section className="py-16 sm:py-20 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
            <div className={`grid gap-10 items-center ${clientLogo ? "md:grid-cols-[1fr_auto]" : ""}`}>
              <div className="relative pl-6 border-l-[3px] border-primary/20 max-w-3xl">
                <div className="prose prose-lg max-w-none prose-p:text-foreground/75 prose-p:leading-[1.9] prose-p:text-[1.0625rem] prose-strong:text-foreground prose-strong:font-semibold">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {sections.summary}
                  </ReactMarkdown>
                </div>
              </div>
              {clientLogo && (
                <div className="flex md:justify-end justify-start">
                  <img
                    src={clientLogo.src}
                    alt={clientLogo.alt}
                    className="w-48 sm:w-56 md:w-64 h-auto object-contain"
                    loading="lazy"
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Steps overview */}
      <section className="py-14 sm:py-18 bg-secondary/50">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {sectionConfig.map((cfg, i) => (
              <motion.a
                key={cfg.key}
                href={`#${cfg.key}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.45, ease: "easeOut" }}
                className="group relative flex items-center gap-5 p-5 sm:p-6 rounded-2xl bg-background border border-border/40 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
              >

                <div className="w-12 h-12 flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110">
                  <cfg.icon size={28} style={{ color: cfg.color }} strokeWidth={1.4} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-base font-bold text-foreground block">{cfg.label}</span>
                  <span className="text-xs text-muted-foreground mt-0.5 block">{cfg.subtitle}</span>
                </div>
                <ChevronRight size={16} className="text-muted-foreground/40 group-hover:text-foreground/50 transition-colors shrink-0" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Content sections */}
      {sectionConfig.map((cfg, i) => {
        const content = sections[cfg.key];
        if (!content) return null;

        return (
          <section
            key={cfg.key}
            id={cfg.key}
            className={`py-16 sm:py-24 scroll-mt-24 ${i % 2 === 0 ? 'bg-background' : 'bg-secondary/30'}`}
          >
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-12 h-12 flex items-center justify-center shrink-0">
                    <cfg.icon size={32} style={{ color: cfg.color }} strokeWidth={1.4} />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{cfg.label}</h2>
                  </div>
                  <div className="flex-1 h-px bg-border/50 ml-4 hidden sm:block" />
                </div>

                <div className={`pl-0 sm:pl-16 border-l-0 sm:border-l-2 ${cfg.accentBorder}/20 sm:ml-6`}>
                  <div className="sm:pl-8">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {content}
                    </ReactMarkdown>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        );
      })}

      {/* Testimonial quote */}
      {sections.quote && (
        <section className="py-24 sm:py-32 bg-muted/40">
          <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div
                aria-hidden="true"
                className="font-serif leading-none select-none mb-2 text-[7rem] sm:text-[9rem] lg:text-[11rem]"
                style={{ color: "#002855" }}
              >
                “
              </div>
              <blockquote
                className="text-lg sm:text-xl lg:text-2xl font-bold leading-snug"
                style={{ color: "#002855" }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => <p className="mb-0">{children}</p>,
                    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                  }}
                >
                  {sections.quote}
                </ReactMarkdown>
              </blockquote>
              {sections.quoteAttribution && (
                <p className="mt-10 text-xs sm:text-sm font-semibold text-muted-foreground uppercase tracking-[0.2em]">
                  {sections.quoteAttribution}
                </p>
              )}
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CaseStudyLayout;